import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Link, { LinkProps } from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import weekday from 'dayjs/plugin/weekday';
import { t } from 'i18next';
import { isEmpty, isNull } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CompanyLogo from '~components/CompanyLogo/CompanyLogo';
import { CopyToClipboard } from '~components/CopyToClipboard/CopyToClipboard';
import HelpCenterLauncher from '~components/HelpCenter/HelpCenterLauncher';
import IntercomLauncher from '~components/Intercom/IntercomLauncher';
import { NavigationTabs } from '~components/LayoutComponents/NavigationTabs';
import UnreadCountBadge from '~components/Messaging/UnreadCountBadge';
import TabLabelWithAdornment from '~components/Tabs/TabLabelWithAdornment';
import { TREAD_PRIVACY_POLICY_LINK, TREAD_TERMS_LINK } from '~constants/consts';
import { SystemRoles } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import useDocumentTitle from '~hooks/useDocumentTitle';
import useLogin from '~hooks/useLogin/useLogin';
import { RoleItem, User } from '~hooks/useUsers/models';
import useVersion from '~hooks/useVersion/useVersion';
import Logo from '~icons/Logo';
import CompanySelector from '~layout/AppHeader/CompanySelector/CompanySelector';
import useCompanySelector from '~layout/AppHeader/CompanySelector/hooks/useCompanySelector';
import useMessagingUnreadCountsQuery from '~queries/useMessagingUnreadCountsQuery';
import { ROUTES } from '~router/routePaths';
import { getRedirectPathForNonAdmin } from '~router/routeUtils';
import {
  hasActiveSession,
  isImpersonating,
  stopImpersonation,
} from '~services/sessionTokens';
import { useStores } from '~store';
import UserStore from '~store/UserStore';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { isProductionEnv } from '~utils/isProductionEnv';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(weekday);

dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    m: '1 min',
    mm: '%d min',
    h: '1 h',
    hh: '%d h',
    d: '1 d',
    dd: '%d d',
    M: '1 mo',
    MM: '%d mo',
    y: '1 y',
    yy: '%d y',
  },
});

// The global navigation bar shares the header's dense MUI Toolbar row. That row
// renders at 64px because the account-menu box (rendered further down) is a
// 64px-tall flex child that stretches the Toolbar past its dense 48px
// min-height. This constant is the single source of truth for that row height:
// it drives both the account-menu box and the nav Tab height, so filling each
// Tab to it makes the whole visible band over a tab a clickable <Link> anchor —
// eliminating dead clicks on the tablist that FullStory measured.
const GLOBAL_NAV_ROW_HEIGHT_PX = 64;

// The selected/hover highlight ("pill") keeps its original compact height and is
// drawn as a centered overlay behind the label, so the tall clickable anchor
// does not stretch the highlight to the full row height.
const GLOBAL_NAV_PILL_HEIGHT_PX = 32;

// pl matches the anchor button's own pl so row text sits under the display name
const accountMenuItemSx = { pl: 4, pr: 2, py: 0.75 };

export const getNavItems = (
  user: User,
  isPlatformAdmin: boolean,
  isImpersonatingUser: boolean,
  isInAppMessagingEnabled: boolean,
  isViewOnlyUser: boolean,
  userAccess: ReturnType<typeof UserStore.prototype.getAccess>,
  messagesUnreadTotal: number,
) => {
  const list = [];

  if (!isViewOnlyUser && userAccess.salesTab) {
    list.push({
      title: t('page_headings.projects'),
      link: ROUTES.projects,
    });
  }

  if (userAccess.dispatchTab) {
    list.push({
      title: t('page_headings.dispatch'),
      link: ROUTES.dispatch.base,
    });
  }

  if (userAccess.assignedTab) {
    list.push({
      title: t('page_headings.assigned'),
      link: ROUTES.assigned,
    });
  }

  if (!isViewOnlyUser && userAccess.approvalsTab) {
    list.push({
      title: t('page_headings.approvals'),
      link: ROUTES.approvals.base,
    });
  }

  if (userAccess.liveMapTab) {
    list.push({
      title: t('page_headings.live_map'),
      link: ROUTES.live,
    });
  }

  if (isInAppMessagingEnabled) {
    list.push({
      title: t('page_headings.messages'),
      link: ROUTES.messages,
      endAdornment: <UnreadCountBadge count={messagesUnreadTotal} />,
    });
  }

  if (isPlatformAdmin && !isImpersonatingUser) {
    list.unshift({
      title: 'Admin',
      link: ROUTES.admin.base,
    });
  }

  if (user.userRoles?.some((role: RoleItem) => role.name === SystemRoles.REPORTING)) {
    list.push({
      title: t('page_headings.reports'),
      link: ROUTES.reports,
    });
  }

  if (userAccess.manageSites) {
    list.push({
      title: t('administration.site.title'),
      link: `${ROUTES.settings.base}/${ROUTES.settings.sitesManagement}`,
    });
  }

  if (userAccess.settingsTab) {
    list.push({
      title: t('page_headings.settings'),
      link: ROUTES.settings.base,
    });
  }

  return list;
};

const ResponsiveAppBar = () => {
  const { userStore } = useStores();
  const navigate = useNavigate();
  const theme = useTheme();
  const { logOut } = useLogin();
  const { version } = useVersion();
  const { showCompanySelector, handleCompanySelectorChange } = useCompanySelector();
  const isImpersonatingUser = isImpersonating();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleNavItemClick = () => {
    setMobileDrawerOpen(false);
  };

  const isPlatformAdmin = useMemo(
    () =>
      userStore.user.userRoles?.some(
        (role: RoleItem) => role.name === SystemRoles.PLATFORM_ADMIN,
      ) ?? false,
    [userStore.user.userRoles],
  );

  const inAppMessagingFeatureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.inAppMessaging,
  });

  const handleLogout = () => {
    handleClose();
    void logOut().catch(() => {
      // Errors are handled in useLogin.
    });
  };

  const handleStopImpersonating = () => {
    handleClose();
    stopImpersonation();
  };
  let timeDiffDisplay = '';
  if (version.time) {
    // Use the duration plugin to convert milliseconds into days and hours
    const versionDate = dayjs.utc(version.time);
    timeDiffDisplay = versionDate.fromNow();
  }

  const userRolesAndCompany = useMemo(() => {
    const { userRoles } = userStore.user ?? {};
    const { legalName } = userStore.userCompany ?? {};

    if (!legalName) {
      return null;
    }

    if (!userRoles || userRoles.length === 0) {
      return legalName;
    }

    const allRoleNames = userRoles.map((role) => role.name).join(', ');
    return `${legalName} • ${allRoleNames}`;
  }, [userStore.user?.userRoles, userStore.userCompany?.legalName]);

  const showsAccountBlock =
    !isEmpty(userStore.user) && (isImpersonatingUser || !isNull(userRolesAndCompany));

  const { unreadCounts } = useMessagingUnreadCountsQuery({
    isEnabled: inAppMessagingFeatureFlag.isFeatureFlagEnabled,
  });
  const messagesUnreadTotal = unreadCounts.total;
  useDocumentTitle(messagesUnreadTotal);

  const userAccess = userStore.getAccess();
  const navItems = useMemo(
    () =>
      getNavItems(
        userStore.user,
        isPlatformAdmin,
        isImpersonatingUser,
        inAppMessagingFeatureFlag.isFeatureFlagEnabled,
        userStore.isViewOnlyUser,
        userAccess,
        messagesUnreadTotal,
      ),
    [
      userStore.user.userRoles,
      isPlatformAdmin,
      isImpersonatingUser,
      inAppMessagingFeatureFlag.isFeatureFlagEnabled,
      userStore.isViewOnlyUser,
      userAccess,
      messagesUnreadTotal,
    ],
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // left untouched on close so the menu's width doesn't snap to undefined
  // mid-close-transition, once anchorEl itself is cleared
  const accountMenuWidthRef = useRef<number>();
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    accountMenuWidthRef.current = event.currentTarget.offsetWidth;
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // If the user is not logged in, we show a simple app bar with the logo.
  // No hook may be called below this early return: the logged-in render would
  // execute more hooks than the logged-out one, and clearing the token while
  // mounted would crash with React error 300 (HORIZON-FE-2RB).
  if (!hasActiveSession()) {
    return (
      <AppBar
        position="static"
        sx={{
          backgroundColor: 'background.paper',
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: '100%',
            width: '100%',
          }}
        >
          <Toolbar variant="regular" sx={{ px: 3 }}>
            <Logo height={24} />
          </Toolbar>
        </Box>
      </AppBar>
    );
  }

  return (
    <AppBar
      data-test-id="app-header"
      position="static"
      sx={{
        backgroundColor: 'background.paper',
        boxShadow: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ maxWidth: '100%', width: '100%' }}>
        <Toolbar
          disableGutters
          variant="dense"
          sx={{ pl: 3, pr: showsAccountBlock ? 0 : 3 }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { xs: 'flex', md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
          {userStore.userCompany.legalName?.length && (
            <Box
              style={{ maxWidth: '100px', marginRight: '24px' }}
              onClick={() => navigate(`${ROUTES.base}`)}
              sx={{
                display: 'inline-flex', // Changed from { xs: 'none', md: 'inline-flex' }
                alignItems: 'center',
                mr: 1,
                cursor: 'pointer',
              }}
            >
              {userStore.userCompany?.logoUrl?.length ? (
                <CompanyLogo
                  maxHeight={32}
                  src={`${userStore.userCompany.logoPreviewUrl || userStore.userCompany.logoUrl}`}
                />
              ) : (
                <Logo height={32} width={168} />
              )}
            </Box>
          )}

          <Box
            sx={{
              flexGrow: 1,
              alignItems: 'center',
              display: { xs: 'none', md: 'flex' },
              borderRight: `1px solid ${theme.palette.divider}`,
              // Hide the default yellow underline indicator
              '& .MuiTabs-indicator': { display: 'none' },
              // Tab styling: muted inactive, dark active with background pill.
              // Each Tab fills the full header row height so the entire visible
              // band over the tab is part of the <Link> anchor (no dead clicks
              // above/below the label). The pill highlight is drawn as a compact
              // centered overlay via ::before, so it keeps its original height
              // instead of stretching to the full row.
              '& .MuiTab-root': {
                position: 'relative',
                // Establish a stacking context so the ::before pill (zIndex -1)
                // paints behind the label but in front of the transparent root.
                zIndex: 0,
                minHeight: GLOBAL_NAV_ROW_HEIGHT_PX,
                fontSize: '13px',
                fontWeight: 500,
                color: theme.brandV2.colors.treadGray2,
                px: 1.5,
                mx: 0.25,
                transition: 'color 150ms ease',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: GLOBAL_NAV_PILL_HEIGHT_PX,
                  borderRadius: theme.brandV2.borderRadius,
                  backgroundColor: 'transparent',
                  transition: 'background-color 150ms ease',
                  zIndex: -1,
                },
                '&:hover::before': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                },
                '&.Mui-selected': {
                  fontWeight: 600,
                  color: theme.brandV2.colors.treadBlack,
                },
                '&.Mui-selected::before': {
                  backgroundColor: theme.brandV2.colors.treadGray8,
                },
              },
            }}
          >
            <NavigationTabs
              navItems={navItems}
              initialValue={
                isPlatformAdmin
                  ? ROUTES.admin.base
                  : getRedirectPathForNonAdmin(userAccess.dispatchTab)
              }
            />
          </Box>

          {showsAccountBlock && (
            <Box
              sx={{
                height: GLOBAL_NAV_ROW_HEIGHT_PX,
                display: 'flex',
                alignItems: 'center',
                ml: 'auto',
              }}
            >
              <Box
                sx={{
                  borderRight: `1px solid ${theme.palette.divider}`,
                }}
              >
                <HelpCenterLauncher />
              </Box>

              {showCompanySelector && (
                <CompanySelector handleChange={handleCompanySelectorChange} />
              )}

              {isProductionEnv() && (
                <Box
                  sx={{
                    borderRight: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <IntercomLauncher />
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  cursor: 'pointer',
                  // full row height so the menu opens below the header, not over the text
                  height: '100%',
                  // pl only, no ml: this box is the account-menu anchor, so its left
                  // edge must sit flush against the divider for the dropdown to align
                  pl: 4,
                  // takes over the toolbar's right padding so the menu reaches the
                  // window edge like the other top-level items
                  pr: 3,
                }}
                onClick={handleClick}
              >
                <Box sx={{ maxWidth: 170, flexDirection: 'column' }}>
                  <Tooltip title={userStore.user.fullName}>
                    <Typography
                      noWrap
                      data-sentry-mask
                      sx={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: theme.brandV2.colors.treadBlack,
                      }}
                    >
                      {userStore.user.fullName}
                    </Typography>
                  </Tooltip>

                  <Tooltip title={userRolesAndCompany}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: '12px',
                        color: theme.brandV2.colors.treadGray3,
                      }}
                    >
                      {userRolesAndCompany}
                    </Typography>
                  </Tooltip>
                </Box>
                <ExpandMore
                  sx={{
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s ease',
                    ml: 0.5,
                    fontSize: 18,
                    color: theme.brandV2.colors.treadGray4,
                  }}
                />
              </Box>
            </Box>
          )}
        </Toolbar>
      </Box>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        marginThreshold={0}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 9,
            sx: {
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              mt: '1px',
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              // matches the anchor's rendered width instead of a fixed value, so it
              // aligns with the button; floored so a short name/role can't shrink it
              // below what the longest row needs to stay on one line
              width: accountMenuWidthRef.current,
              minWidth: 200,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            navigate(`${ROUTES.settings.base}/${ROUTES.settings.userProfile}`);
          }}
          sx={accountMenuItemSx}
        >
          <Typography sx={{ color: theme.palette.text.secondary }}>
            {t('app_header.profile')}
          </Typography>
        </MenuItem>

        {isImpersonatingUser && (
          <MenuItem onClick={handleStopImpersonating} sx={accountMenuItemSx}>
            <Typography sx={{ color: theme.palette.text.secondary }}>
              {t('app_header.stop_impersonating')}
            </Typography>
          </MenuItem>
        )}

        <MenuItem onClick={handleLogout} sx={accountMenuItemSx}>
          <Typography sx={{ color: theme.palette.text.secondary }}>
            {t('page_headings.logout')}
          </Typography>
        </MenuItem>

        <Divider sx={{ bgcolor: theme.palette.divider }} />

        <MenuItem sx={accountMenuItemSx}>
          <CopyToClipboard
            // flex-start (not the default space-between) no longer separates the
            // "Copied!" alert from the label on its own, so gap fills that in
            sx={{ width: 'auto', justifyContent: 'flex-start', gap: 1 }}
            textToCopy={userStore.userCompany.treadId}
          >
            <Typography sx={{ fontSize: '12px', color: theme.brandV2.colors.treadBlack }}>
              <Typography
                component="span"
                sx={{ fontSize: 'inherit', color: theme.brandV2.colors.treadGray3 }}
              >
                {t('page_headings.tread_id')}:
              </Typography>
              <Typography component="span" sx={{ fontSize: 'inherit' }}>
                {` ${userStore.userCompany.treadId}`}
              </Typography>
            </Typography>
          </CopyToClipboard>
        </MenuItem>

        <MenuItem disableRipple sx={accountMenuItemSx}>
          <Typography sx={{ fontSize: '12px', color: theme.brandV2.colors.treadBlack }}>
            <Typography
              component="span"
              sx={{ fontSize: 'inherit', color: theme.brandV2.colors.treadGray3 }}
            >
              {t('page_headings.last_release')}:
            </Typography>
            <Typography component="span" sx={{ fontSize: 'inherit' }}>
              {` ${timeDiffDisplay}`}
            </Typography>
          </Typography>
        </MenuItem>

        <MenuItem disableRipple sx={accountMenuItemSx}>
          <Typography sx={{ fontSize: '12px', color: theme.brandV2.colors.treadBlack }}>
            <StyledLink href={TREAD_TERMS_LINK} rel="noopener noreferrer" target="_blank">
              {t('app_header.terms')}
            </StyledLink>
            {' • '}
            <StyledLink
              href={TREAD_PRIVACY_POLICY_LINK}
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('app_header.privacy_policy')}
            </StyledLink>
          </Typography>
        </MenuItem>
      </Menu>

      {/* Mobile navigation drawer */}
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }} // Better performance on mobile
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '100%' },
        }}
      >
        {/* Drawer header with close button and logo */}
        <Box sx={{ pt: 2, pb: 2, display: 'flex', justifyContent: 'flex-start', pl: 2 }}>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{ color: theme.palette.primary.contrastText }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', pr: 4 }}>
            {userStore.userCompany?.logoUrl?.length ? (
              <CompanyLogo
                maxHeight={32}
                src={`${userStore.userCompany.logoPreviewUrl || userStore.userCompany.logoUrl}`}
              />
            ) : (
              <Logo height={32} width={168} />
            )}
          </Box>
        </Box>
        <Divider sx={{ bgcolor: theme.palette.divider }} />
        {/* Navigation items */}
        <List>
          {navItems.map((navItem, index) => (
            <ListItem key={index} disablePadding>
              <ListItemButton
                onClick={() => {
                  handleNavItemClick();
                  navigate(navItem.link);
                }}
                sx={{ color: theme.palette.primary.contrastText }}
              >
                <ListItemText
                  primary={
                    <TabLabelWithAdornment
                      endAdornment={navItem.endAdornment}
                      label={navItem.title}
                    />
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </AppBar>
  );
};

const StyledLink = styled(Link)<LinkProps>(() => ({
  fontSize: 'inherit',
  color: 'inherit',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
}));

export default observer(ResponsiveAppBar);
